/**
 * @file Customers list view — customer lookup, search, add
 */

import {
  getCustomers, searchCustomers, addCustomer, getCustomerJobs,
  getCustomerRevenue, timeAgo
} from '@services/customers.js';
import { push } from '@core/router.js';
import { showModal, closeModal } from '@components/modal.js';
import { toast } from '@components/toast.js';

let _searchTerm = '';
let _sortBy = 'alpha';

function getFilteredCustomers() {
  let custs = _searchTerm ? searchCustomers(_searchTerm) : getCustomers();

  if (_sortBy === 'alpha') {
    custs.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  } else if (_sortBy === 'recent') {
    custs.sort((a, b) => (b.lastContacted || b.created || '').localeCompare(a.lastContacted || a.created || ''));
  } else if (_sortBy === 'revenue') {
    custs.sort((a, b) => getCustomerRevenue(b.id) - getCustomerRevenue(a.id));
  }

  return custs;
}

function render() {
  const customers = getFilteredCustomers();
  const totalCount = getCustomers().length;

  return `
    <div class="view-header">
      <div>
        <h1>Customers</h1>
        <div class="view-header-sub">${totalCount} total</div>
      </div>
      <button class="btn btn-gold btn-sm" id="add-customer-btn">+ Add</button>
    </div>

    <div class="search-wrap">
      <span class="search-icon">\uD83D\uDD0D</span>
      <input type="text" id="customers-search" placeholder="Search customers..." value="${esc(_searchTerm)}" />
    </div>

    <div class="leads-sort-row">
      <select id="customers-sort" class="leads-sort-select">
        <option value="alpha"${_sortBy === 'alpha' ? ' selected' : ''}>A-Z</option>
        <option value="recent"${_sortBy === 'recent' ? ' selected' : ''}>Recent</option>
        <option value="revenue"${_sortBy === 'revenue' ? ' selected' : ''}>Revenue</option>
      </select>
      <span class="leads-result-count">${customers.length} customer${customers.length !== 1 ? 's' : ''}</span>
    </div>

    <div class="lead-list" id="customer-list">
      ${customers.length > 0
        ? customers.map(c => renderCustomerCard(c)).join('')
        : `<div class="empty-state">
             <div class="empty-state-icon">\uD83D\uDC64</div>
             <div class="empty-state-text">No customers found</div>
           </div>`
      }
    </div>
  `;
}

function renderCustomerCard(cust) {
  const jobs = getCustomerJobs(cust.id);
  const revenue = getCustomerRevenue(cust.id);
  const contacted = timeAgo(cust.lastContacted || cust.created);
  const displayName = cust.name || `${cust.firstName || ''} ${cust.lastName || ''}`.trim() || 'Unnamed';

  // Determine status badge
  let statusLabel = 'Client';
  let statusBadge = 'badge-green';
  if (jobs.length === 0) {
    statusLabel = 'Lead';
    statusBadge = 'badge-blue';
  }

  return `
    <div class="lead-card customer-card" data-customer-id="${cust.id}">
      <div class="lead-card-top">
        <div class="lead-card-info">
          <div class="lead-card-name">${esc(displayName)}</div>
          ${cust.phone ? `<a href="tel:${esc(cust.phone)}" class="lead-card-phone" onclick="event.stopPropagation();">\uD83D\uDCDE ${esc(cust.phone)}</a>` : ''}
        </div>
        <span class="badge ${statusBadge}">${statusLabel}</span>
      </div>

      <div class="lead-card-meta">
        ${cust.email ? `<span style="font-size:0.72rem;color:var(--gray-400);">${esc(cust.email)}</span>` : ''}
        <span class="lead-card-contacted">${contacted}</span>
      </div>

      <div class="lead-card-meta" style="margin-top:4px;">
        <span style="font-size:0.72rem;color:var(--gray-400);">${jobs.length} job${jobs.length !== 1 ? 's' : ''}</span>
        ${revenue > 0 ? `<span style="font-size:0.72rem;color:var(--gold);font-weight:600;">$${revenue.toLocaleString()}</span>` : ''}
      </div>

      <div class="lead-card-actions">
        ${cust.phone ? `
          <a href="tel:${esc(cust.phone)}" class="lead-quick-btn lead-quick-call" onclick="event.stopPropagation();" title="Call">
            <span>\uD83D\uDCDE</span>
            <span>Call</span>
          </a>
          <a href="sms:${esc(cust.phone)}" class="lead-quick-btn lead-quick-text" onclick="event.stopPropagation();" title="Text">
            <span>\u2709\uFE0F</span>
            <span>Text</span>
          </a>
        ` : ''}
        ${cust.address ? `
          <a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cust.address)}" target="_blank" class="lead-quick-btn" onclick="event.stopPropagation();" title="Directions" style="background:rgba(212,175,55,0.12);color:var(--gold);">
            <span>\uD83D\uDCCD</span>
            <span>Map</span>
          </a>
        ` : ''}
      </div>
    </div>
  `;
}

function mount() {
  // Search
  const search = document.getElementById('customers-search');
  if (search) {
    search.addEventListener('input', (e) => {
      _searchTerm = e.target.value;
      refreshView();
    });
  }

  // Sort
  const sort = document.getElementById('customers-sort');
  if (sort) {
    sort.addEventListener('change', (e) => {
      _sortBy = e.target.value;
      refreshView();
    });
  }

  // Add customer
  document.getElementById('add-customer-btn')?.addEventListener('click', showAddCustomerModal);

  // Card taps
  const list = document.getElementById('customer-list');
  if (list) {
    list.addEventListener('click', (e) => {
      if (e.target.closest('a')) return;
      const card = e.target.closest('.customer-card');
      if (card) push('customer-detail', { customerId: card.dataset.customerId });
    });
  }
}

function showAddCustomerModal() {
  showModal('Add Customer', `
    <div class="form-group">
      <label>Name *</label>
      <input type="text" id="new-cust-name" placeholder="Full name" />
    </div>
    <div class="form-group">
      <label>Phone</label>
      <input type="tel" id="new-cust-phone" placeholder="(555) 123-4567" />
    </div>
    <div class="form-group">
      <label>Email</label>
      <input type="email" id="new-cust-email" placeholder="email@example.com" />
    </div>
    <div class="form-group">
      <label>Address</label>
      <input type="text" id="new-cust-address" placeholder="Street address" />
    </div>
    <div class="form-group">
      <label>Notes</label>
      <textarea id="new-cust-notes" rows="2" placeholder="Notes..."></textarea>
    </div>
  `, [
    { label: 'Cancel', onClick: closeModal },
    { label: 'Save', type: 'gold', onClick: () => {
      const name = document.getElementById('new-cust-name')?.value?.trim();
      if (!name) { toast('Name is required', 'error'); return; }

      addCustomer({
        name,
        phone: document.getElementById('new-cust-phone')?.value?.trim() || '',
        email: document.getElementById('new-cust-email')?.value?.trim() || '',
        address: document.getElementById('new-cust-address')?.value?.trim() || '',
        notes: document.getElementById('new-cust-notes')?.value?.trim() || '',
      });

      closeModal();
      toast(`${name} added`, 'success');
      refreshView();
    }},
  ]);

  setTimeout(() => document.getElementById('new-cust-name')?.focus(), 200);
}

function refreshView() {
  const container = document.getElementById('view-container');
  if (container) {
    container.innerHTML = render();
    mount();
  }
}

function unmount() {}

function esc(str) {
  if (!str) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export default { render, mount, unmount };
